<script setup>
/*
 * "Hvilken PC skal du bruge til <spil>".
 *
 * Siden er bygget i den raekkefoelge spoergsmaalet faktisk stilles:
 * hvad siger udgiveren, hvad betyder det i praksis, og hvilken maskine
 * svarer til hvert trin.
 *
 * KRAVENE STAAR SOM CITAT. De er udgiverens egne tal med kilde og dato
 * nedenunder, og de maa ikke blandes sammen med vores vurdering — den
 * staar i afsnittene og i begrundelsen paa hver maskine.
 */
import SEOHead from '@/Components/SEO/SEOHead.vue';
import Blokliste from '@/Components/Blokke/Blokliste.vue';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import Buildkort from '@/Components/PcBygger/Buildkort.vue';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps({
    side: { type: Object, required: true },
    maskiner: { type: Array, default: () => [] },
    blokke: { type: Array, default: () => [] },
});

// Kolonnerne i kravskemaet. Kun dem der faktisk er udfyldt paa mindst ét
// trin — Valve opgiver ingen VRAM, og en tom raekke er stoej.
const RAEKKER = [
    ['os', 'Styresystem'],
    ['cpu', 'Processor'],
    ['gpu', 'Grafik'],
    ['vram', 'Grafikhukommelse'],
    ['ram', 'Hukommelse'],
    ['lager', 'Plads'],
    ['note', 'Andet'],
];

const raekker = computed(() =>
    RAEKKER.filter(([n]) => props.side.krav.some((t) => t[n]))
);
</script>

<template>
    <PriceCompareLayout>
        <SEOHead />

        <div class="container-site spilside">
            <nav class="spil-sti" aria-label="Brødkrumme">
                <Link href="/spil-pc">Spil og PC</Link>
                <span aria-hidden="true">›</span>
                <span>{{ side.navn }}</span>
            </nav>

            <header class="spil-top">
                <h1>Hvilken PC skal du bruge til {{ side.navn }}?</h1>
                <p class="spil-meta">
                    <span v-if="side.udgiver">{{ side.udgiver }}</span>
                    <span v-if="side.genre"> &middot; {{ side.genre }}</span>
                    <span v-if="side.udgivelsesaar"> &middot; {{ side.udgivelsesaar }}</span>
                </p>
                <p class="spil-manchet">{{ side.manchet }}</p>
            </header>

            <section class="spil-krav" aria-labelledby="krav-overskrift">
                <h2 id="krav-overskrift">De officielle systemkrav</h2>

                <div class="krav-rul">
                    <table class="krav-tabel">
                        <thead>
                            <tr>
                                <th scope="col"><span class="visuelt-skjult">Krav</span></th>
                                <th v-for="t in side.krav" :key="t.trin" scope="col">
                                    {{ t.trin }}
                                    <small v-if="t.maal">{{ t.maal }}</small>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="[noegle, navn] in raekker" :key="noegle">
                                <th scope="row">{{ navn }}</th>
                                <td v-for="t in side.krav" :key="t.trin + noegle">{{ t[noegle] || '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!--
                    Kilde og dato skal staa der. Kravene aendrer sig — Siege X
                    hoevede sine markant i 2026 — og et tal uden en dato er
                    ikke til at efterproeve.
                -->
                <p class="krav-kilde">
                    Kravene er {{ side.udgiver }}s egne.
                    <a v-if="side.krav_kilde" :href="side.krav_kilde" rel="nofollow noopener" target="_blank">Se kilden</a>
                    <span v-if="side.krav_hentet"> &middot; efterset {{ side.krav_hentet }}</span>
                </p>
            </section>

            <section v-if="maskiner.length" class="spil-maskiner" aria-labelledby="maskiner-overskrift">
                <h2 id="maskiner-overskrift">Maskiner der lever op til kravene</h2>
                <p class="spil-ingress">
                    Priserne er dagens laveste hos de danske forhandlere vi følger. Hver del kan klikkes,
                    og hele maskinen kan åbnes i PC-byggeren hvis du vil skifte noget ud.
                </p>

                <div class="maskin-gitter">
                    <Buildkort v-for="m in maskiner" :key="m.rolle"
                        :kort="m.kort" :etiket="m.etiket" :begrundelse="m.begrundelse"
                        :fremhaevet="m.rolle === 'anbefalet'" />
                </div>
            </section>

            <section v-if="side.afsnit.length" class="spil-tekst">
                <article v-for="a in side.afsnit" :key="a.overskrift">
                    <h2>{{ a.overskrift }}</h2>
                    <p>{{ a.tekst }}</p>
                </article>
            </section>

            <section v-if="side.faq.length" class="spil-faq" aria-labelledby="faq-overskrift">
                <h2 id="faq-overskrift">Ofte stillede spørgsmål</h2>
                <details v-for="f in side.faq" :key="f.spoergsmaal">
                    <summary>{{ f.spoergsmaal }}</summary>
                    <p>{{ f.svar }}</p>
                </details>
            </section>

            <!-- Indholdsblokke. Tom paa de sider der ikke har faaet nogen. -->
            <Blokliste v-if="blokke.length" :blokke="blokke" class="mb-4" />

            <aside class="spil-videre">
                <h2>Byg din egen i stedet</h2>
                <p>
                    Maskinerne ovenfor er et afsæt, ikke en facitliste. I PC-byggeren kan du skifte
                    delene ud og få at vide undervejs om de passer sammen — sokkel, hukommelse, plads
                    i kabinettet og strøm.
                </p>
                <Link href="/pc-bygger" class="btn btn-pricecompare-primary">Åbn PC-byggeren</Link>
            </aside>
        </div>
    </PriceCompareLayout>
</template>

<style scoped>
.spilside {
    /*
     * Kun top og bund.
     *
     * `padding: 1.5rem 0 3rem` er en shorthand, og den nulstiller
     * ogsaa venstre og hoejre — paa det SAMME element som
     * .container-site, som netop er den der giver sidens indryk
     * paa 24 px. Overskriften stod derfor klinet op ad kanten.
     */
    padding-top: 1.5rem;
    padding-bottom: 3rem; display: flex; flex-direction: column; gap: 2.25rem; }

.spil-sti { display: flex; gap: .5rem; font-size: .85rem; color: var(--pc-text-muted); }
.spil-sti a { color: var(--pc-text-secondary); text-decoration: none; }
.spil-sti a:hover { color: var(--pc-accent); }

.spil-top h1 { font-size: clamp(1.6rem, 4vw, 2.3rem); font-weight: 700; margin: 0 0 .4rem; }
.spil-meta { font-size: .85rem; color: var(--pc-text-muted); margin: 0 0 .8rem; }
.spil-manchet { font-size: 1.05rem; line-height: 1.6; max-width: 62ch; margin: 0; }

h2 { font-size: 1.3rem; font-weight: 700; margin: 0 0 .8rem; }

.krav-rul { overflow-x: auto; }
.krav-tabel { width: 100%; border-collapse: collapse; min-width: 34rem; }
.krav-tabel th, .krav-tabel td {
    text-align: left; padding: .6rem .8rem;
    border-bottom: 1px solid var(--pc-border); vertical-align: top;
}
.krav-tabel thead th { background: var(--pc-bg-tertiary); font-size: .9rem; }
.krav-tabel thead th small { display: block; font-weight: 400; color: var(--pc-text-muted); font-size: .75rem; }
.krav-tabel tbody th { width: 11rem; font-weight: 500; color: var(--pc-text-secondary); font-size: .85rem; }
.krav-tabel tbody td { font-size: .88rem; }
.krav-kilde { font-size: .8rem; color: var(--pc-text-muted); margin: .6rem 0 0; }
.krav-kilde a { color: var(--pc-text-secondary); }

.spil-ingress { font-size: .92rem; color: var(--pc-text-secondary); max-width: 62ch; margin: 0 0 1rem; }
.maskin-gitter { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(19rem, 1fr)); }

.spil-tekst { display: flex; flex-direction: column; gap: 1.5rem; }
.spil-tekst p { line-height: 1.7; max-width: 66ch; margin: 0; }

.spil-faq details {
    border-bottom: 1px solid var(--pc-border);
    padding: .8rem 0;
}
.spil-faq summary { cursor: pointer; font-weight: 600; }
.spil-faq p { margin: .6rem 0 0; line-height: 1.6; max-width: 66ch; color: var(--pc-text-secondary); }

.spil-videre {
    background: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg);
    padding: 1.4rem;
}
.spil-videre p { max-width: 62ch; line-height: 1.6; }

.visuelt-skjult {
    position: absolute; width: 1px; height: 1px;
    padding: 0; margin: -1px; overflow: hidden;
    clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
</style>
